// Third Controls
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRealtimeDrivers } from 'Core/hooks';
import DoneIcon from '@mui/icons-material/Done';
import { Grid } from '@mui/material';

// Components
import EntitySelector from '../../../../../modules/.common/components/EntitySelector';
import { Button, Form, Input, Loading } from 'Components';
import { NotificationManager } from "Components/Notifications";
import TagsManage from 'Modules/.common/components/TagsManage';

import Pill from "Components/Pill";
// Actions
import {
  catalogLoad,
  sendMessage,
  sendMessageResponse
} from 'Redux/actions';

import './style.scss';

const SendMessage = (props) => {
  const { messages } = props;
  const dispatch = useDispatch();
  const formSetup = useRef();

  const entities = [
    { id: "Contact", label: messages["realtimemaps_send_message_contacts"] },
    { id: "Driver", label: messages["realtimemaps_send_message_drivers"] }
  ];

  const entitiesTagManage = useMemo(
    () => [
      { id: "Driver", label: messages["realtimemaps_send_message_drivers"] },
    ],
    [messages]
  );

  const { currentModule } = useSelector(state => state.realtimeMapUtilitiesRedux);
  const { loading, sendMessage: response } = useSelector(state => state.realtimeMapSendMessageModuleRedux);
  const drivers = useRealtimeDrivers({ trackFields: ['driverId', 'name', 'tagIds'] });

  const driversList = useMemo(() => {
    if (!drivers) return [];

    return drivers.map(driver => ({
      driverId: driver.driverId,
      name: driver.name,
      tagIds: typeof driver.tagIds === 'string'
        ? JSON.parse(driver.tagIds)
        : driver.tagIds || []
    }));
  }, [drivers]);

  const [selectedEntity, setSelectedEntity] = useState({ id: "contacts", label: messages["realtimemaps_send_message_contacts"] });
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsError, setItemsError] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [entity, setEntity] = useState(entities[0]);
  const [loadingTags, setLoadingTags] = useState(false)
  const [tempFilters, setTempFilters] = useState({});
  const [entitySelected, setEntitySelected] = useState([]);


  // Callback to handle changes in TagsManage
  const onChangeEntityTagPicker = useCallback(
    (items, entity) => {
      let newItems = [];

      if (items.length) {
        newItems = items.filter((n) => n.id != -1);
      } else {
        newItems = [];
      }

      // Separate tags and drivers according to the "name" field
      const tags = newItems.filter(item => item.name === "Tag");
      const drivers = newItems.filter(item => item.name === "Driver");

      // Update tempFilters with separated tags and drivers
      setTempFilters({
        ...tempFilters,
        tags: tags.map(tag => ({ id: tag.id, label: tag.label })),
        drivers: drivers.map(driver => ({
          driverId: driver.id,
          name: driver.label,
        }))
      });
      setItemsError(false);
      setEntitySelected(newItems);
    },
    [tempFilters]
  );

  const handleSendMessage = () => {
    setItemsError(false);

    let newSelectedItems = [...selectedItems];

    if (newSelectedItems.length > 0) {
      let tags = selectedItems.filter(i => i.name === "Tag");
      if (tags.length) {
        let filteredItems = [];
        let tagIds = tags.map(t => {
          return parseInt(t.id);
        });

        selectedItems.filter(i => i.name !== "Tag").forEach(item => {
          if (!tagIds.includes(parseInt(item.parentTagId))) {
            filteredItems.push(item);
          }
        });

        newSelectedItems = [...filteredItems.concat(tags)];
      }
    } else if (entitySelected.length > 0) { // For TagManage selection

      // Filter only manually selected drivers
      const selectedDrivers = entitySelected.filter(item => item.name === "Driver");

      // Get selected tags
      const selectedTags = entitySelected.filter(item => item.name === "Tag");

      if (selectedTags.length > 0) {
        const selectedTagIds = selectedTags.map(tag => parseInt(tag.id));

        // Search for drivers that belong to the selected tags
        const driversFromTags = driversList.filter(driver => {
          if (!driver.tagIds) return false;

          let driverTagIds = [];
          try {
            driverTagIds = typeof driver.tagIds === 'string'
              ? JSON.parse(driver.tagIds)
              : driver.tagIds;
          } catch (e) {
            return false;
          }

          return driverTagIds.some(tagId => selectedTagIds.includes(parseInt(tagId)));
        }).map(driver => ({
          id: driver.driverId,
          label: driver.name,
          name: "Driver"
        }));

        // Combine manually selected drivers + drivers from tags
        const allDrivers = [...selectedDrivers, ...driversFromTags];

        // Remove duplicates by id
        const uniqueDrivers = allDrivers.reduce((acc, current) => {
          const exists = acc.find(item => item.id === current.id);
          if (!exists) {
            acc.push(current);
          }
          return acc;
        }, []);

        newSelectedItems = uniqueDrivers;
      } else {
        // If there are no tags, only use manually selected drivers
        newSelectedItems = selectedDrivers;
      }
    }

    let payload = {
      to: newSelectedItems.map((item) => ({ id: parseInt(item?.id), name: entity.id === "Contact" ? item.__typename : item.name })),
      subject,
      message
    }
    dispatch(sendMessage(payload));
  }

  useEffect(() => {
    if (currentModule === 9)
      dispatch(catalogLoad(["contacts", "drivers", "driverGroups"]))
  }, [currentModule]);

  useEffect(() => {
    if (response) {
      let message = null;
      if (response?.message == "Emails/SMS doesn't exists") {
        message = messages['realtimemaps_send_message_sent_error'];
        NotificationManager.warning(message, '', 3000, null, null, '', null);
      } else {
        setSubject('');
        setMessage('');
        setSelectedItems([]);
        NotificationManager.success(messages['realtimemaps_send_message_sent_successfully'], '', 3000, null, null, '', null);
      }
      dispatch(sendMessageResponse(null));
    }
  }, [response])

  return (
    <Form
      ref={formSetup}
      onSubmit={() => {
        if (selectedItems.length == 0 && entitySelected.length == 0) {
          setItemsError(true);
          return false
        } else {
          setItemsError(false);
        }
        return true
      }}
      onValidSubmit={() => handleSendMessage()}
    >
      <Grid container spacing={0} style={{ backgroundColor: 'transparent' }}>
        {loading && <Loading overlay show />}

        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          {
            entities.map((et, index) => {
              return (
                <Grid item xs key={index}>
                  <Pill
                    key={index}
                    textField="label"
                    item={et}
                    outlined={et.id !== entity.id}
                    onClick={() => {
                      setSelectedItems([]);
                      setEntitySelected([]);
                      setEntity(et);
                    }}
                    style={{ border: '0', width: '100%' }}
                    colorField="default"
                    colorSelection="005bf8"
                    isDisabled={loadingTags}
                  />
                </Grid>
              )
            })
          }
        </Grid>

        <Grid item xs={12}>
          {
            entity?.id === "Driver" &&
            <div style={{ marginTop: 10 }}>
              <div className="send-message-tags-manage" style={{ width: '100%', overflow: "auto", marginTop: 5 }}>
                <TagsManage
                  entities={entitiesTagManage}
                  onChange={onChangeEntityTagPicker}
                  hideTagsWithoutEntities={true}
                  mode="list"
                  height={400}
                  value={entitySelected}
                  noPills={true}
                  entitySelected={{ id: "Driver", label: messages["realtimemaps_send_message_drivers"] }}
                />
              </div>

              {
                itemsError &&
                <small className="select-error text-danger">{messages["realtimemaps_send_message_select_option_driver"]}</small>
              }
            </div>
          }

          {
            entity?.id === "Contact" &&
            <div>
              <EntitySelector
                id="contacts"
                title={<strong>{`${messages['realtimemaps_send_message_contact']}:`}</strong>}
                entities={[{ id: "contacts", label: messages["realtimemaps_send_message_contacts"] }]}
                checkedIcon={<DoneIcon style={{ color: "00ADFF" }} />}
                unCheckedIcon={<DoneIcon color="disabled" />}
                requiredMessage={messages["required_field"]}
                selectedEntitiesTitle={messages['reportSetup_selected']}
                onChangeItems={(newItems) => {
                  setItemsError(false);
                  setSelectedItems(newItems);
                  setEntitySelected([]);
                }}
                onChangeEntity={(selectedEntity) => setSelectedEntity(selectedEntity)}
                required
                selectedEntity={selectedEntity}
                selectedItems={selectedItems}
                initialSize={5}
                multiple
              />
              {
                itemsError &&
                <small className="select-error text-danger">{messages["realtimemaps_send_message_select_option_contacts"]}</small>
              }
            </div>
          }
        </Grid>

        <Grid item xs={12}>
          <Input
            id="subject"
            label={`${messages['realtimemaps_send_message_subject']}:`}
            type="text"
            onChange={(value) => setSubject(value)}
            value={subject}
            required
            requiredMessage={messages['required_field']}
            maxLength={50}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            id="message"
            label={`${messages['realtimemaps_send_message_message']}:`}
            type="textarea"
            onChange={(value) => setMessage(value)}
            value={message}
            required
            requiredMessage={messages['required_field']}
            maxLength={200}
            className={'sendMessages-textArea'}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            onClick={(e) => {
              e.preventDefault();
              formSetup.current.submit()
            }}
            disabled={loading}
            className="btn-blue" style={{ width: '100%', marginTop: 15 }}>{messages['realtimemaps_send_message_send']}
          </Button>
        </Grid>
      </Grid>
    </Form>
  )
}

export default SendMessage;